import { CameraModel } from '../models/camera.model';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../environments/environment';
import { ErrorModel } from '../models/error.model';
import { ErrorTypes } from '../shared/error-types.enum';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { IdentityModel } from '../models/identity.model';
import { ImageService } from './image.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SiteModel } from '../models/site.model';
import { SiteService } from './site.service';
import { Subject, Subscription, from } from 'rxjs';
import { UserModel } from '../models/user.model';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { InteractionStatus } from '@azure/msal-browser';

/**
 * This service manages the main application state and functionality
 * including user authentication, site/camera selection, and image loading
 */
@Injectable({
  providedIn: 'root',
})
export class AppService {
  // Properties
  appUser: UserModel;
  identity: IdentityModel;
  autoChangeSite: boolean;
  selectedSite: SiteModel;
  selectedCamera: CameraModel;

  isImageLoading: boolean;
  isImageLoaded: boolean;
  isAppLoaded: boolean;

  doAutoRefresh: boolean = false;
  autoRefreshInt: number = -1;
  private refreshTimeout: number;
  private siteStatusCheckStarted: boolean = false;

  get sites(): SiteModel[] {
    return this.siteService.sites;
  }

  // Events
  private appLoadedSource = new Subject<void>();
  public appLoadedEvent = this.appLoadedSource.asObservable();
  private appErrorSource = new Subject<ErrorModel>();
  public appErrorEvent = this.appErrorSource.asObservable();
  private appRefreshImageSource = new Subject<void>();
  public appRefreshImageEvent = this.appRefreshImageSource.asObservable();
  private readonly destroying$ = new Subject<void>();

  // event handlers
  imageLoadingEvent: Subscription = null;
  imageLoadedEvent: Subscription = null;

  constructor(
    private authService: MsalService,
    private broadcastService: MsalBroadcastService,
    private cookieService: CookieService,
    private http: HttpClient,
    private imageService: ImageService,
    private router: Router,
    private siteService: SiteService
  ) {
    this.autoChangeSite = this.cookieService.get('autoChangeSite') == 'true';
    this.StartApp();
  }
  /**
   * Start the app
   */
  private StartApp() {
    try {
      this.imageService.imageLoadedEvent.subscribe({ next: () => this.onImageLoaded() });
      this.imageService.imageLoadingEvent.subscribe({
        next: () => this.onImageLoading()
      });

      this.broadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          ),
          takeUntil(this.destroying$)
        )
        .subscribe({
          next: () => {
            const isAuthenticated =
              this.authService.instance.getAllAccounts().length > 0;

            if (isAuthenticated) {
              const account = this.authService.instance.getAllAccounts()[0];
              const claims =
                account && (account as any).idTokenClaims
                  ? (account.idTokenClaims as Record<string, unknown>)
                  : ({} as Record<string, unknown>);

              const givenName = String((claims['given_name'] as string) || '');
              const familyName = String((claims['family_name'] as string) || '');
              const email = String((claims['email'] as string) || '');
              const upn = String((claims['upn'] as string) || '');

              this.identity = {
                firstName: givenName,
                lastName: familyName,
                email: email,
                upn: upn,
                displayName: `${givenName} ${familyName}`.trim(),
              };

              this.getUser();
            }
          }
        });
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * When Image Loading
   */
  onImageLoading() {
    this.stopAutoRefresh();
  }

  /**
   * When image loaded
   */
  onImageLoaded() {
    if (this.doAutoRefresh) this.startAutoRefresh();
  }

  /**
   * Begins checking each site's status in the background.
   */
  startSiteStatusCheck() {
    if (this.siteStatusCheckStarted) {
      return;
    }

    if (!this.sites || this.sites.length === 0) {
      return;
    }

    this.siteStatusCheckStarted = true;
    void this.siteService.refreshSitesStatus(this.sites).finally(() => {
      this.siteStatusCheckStarted = false;
    });
  }

  /**
   * Configure auto-refresh for camera images
   * @param {number} interval - Refresh interval in milliseconds. Set to 0 or less to disable auto-refresh.
   */
  configureAutoRefresh(interval: number) {
    if (interval > 0) {
      if (this.selectedCamera) {
        this.autoRefreshInt = interval;
        this.doAutoRefresh = true;
        this.startAutoRefresh();
      }
    } else {
      this.stopAutoRefresh();
      this.doAutoRefresh = false;
      this.autoRefreshInt = -1;
    }
  }

  /**
   * Start the auto-refresh timer to refresh camera images at the configured interval
   */
  private startAutoRefresh() {
    this.refreshTimeout = +setTimeout(() => {
      this.stopAutoRefresh();
      this.refreshCamera(true);
    }, this.autoRefreshInt);
  }

  /**
   * Stop the auto-refresh timer to prevent further automatic camera image refreshes
   */
  private stopAutoRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    this.refreshTimeout = null;
  }

  /**
   * Get the user from database
   * @returns Promise
   */
  private getUser(): Promise<UserModel | void> {
    let request = {
      UPN: this.identity.upn ? this.identity.upn : '',
    };

    return new Promise((resolve, reject) => {
      this.http
        .post<UserModel>(
          `${environment.managementApi}/Users/upn?includeSites=true`,
          request,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .pipe(
          // store user, then load sites list and user sites
          switchMap((user) => {
            this.appUser = user;
            return from(this.siteService.getAllSites(this.appUser.IsAdmin, false));
          }),
          switchMap(() =>
            this.http.get<SiteModel[]>(
              `${environment.managementApi}/Sites/user/${this.appUser.Id}?includeCameras=true`,
            )
          )
        )
        .subscribe({
          next: (userSites: SiteModel[]) => {
            this.siteService.sites = userSites;

            // Run one async pass of site status checks on initial load.
            this.startSiteStatusCheck();

            // send "app loaded" signal
            this.isAppLoaded = true;
            this.appLoadedSource.next();
            resolve();
          },
          error: (err) => {
            // error getting user from database or site load
            if (err instanceof HttpErrorResponse) {
              let rErr = err as HttpErrorResponse;
              if (rErr.status == 410) {
                // Gone - user is not in database
                console.warn(`User not registered: ${this.identity.upn}`);
                this.appErrorSource.next({
                  errorType: ErrorTypes.UserNotRegistered,
                  errorMessage: `The user ${this.identity.upn} is not registered on this system`,
                  redirect: true,
                  redirectPath: ['not-registered'],
                });
                resolve();
              } else {
                console.error(err);
                this.appErrorSource.next({
                  errorType: ErrorTypes.HttpError,
                  errorMessage: `${rErr.status}: ${rErr.statusText}`,
                  redirect: true,
                });
                reject(err);
              }
            } else {
              console.error(err);
              this.appErrorSource.next({
                errorType: ErrorTypes.UnhandledError,
                errorMessage: err.toString(),
                redirect: true,
              });
              reject(err);
            }
          },
        });
    });
  }

  /**
   * Cycle to the next camera, or site if enabled and at end of camera list
   */
  nextCamera() {
    if (
      !this.isImageLoading &&
      (!this.selectedSite ||
        (this.selectedSite && this.selectedSite.Cameras.length > 0))
    ) {
      let currentIndex: number;
      let nextIndex: number;

      if (!this.selectedSite) this.nextSite();
      else {
        if (this.selectedCamera) {
          currentIndex = this.selectedSite.Cameras.indexOf(this.selectedCamera);
          nextIndex = currentIndex + 1;
          if (nextIndex >= this.selectedSite.Cameras.length) {
            if (this.autoChangeSite) nextIndex = -1;
            else nextIndex = 0;
          }
        } else nextIndex = 0;

        if (nextIndex == -1) this.nextSite();
        else {
          this.selectedCamera = this.selectedSite.Cameras[nextIndex];
          this.router.navigate([
            '/view',
            this.selectedSite.Id,
            this.selectedCamera.Id,
          ]);
        }
      }
    }
  }

  /**
   * Cycle to the previous camera, or site if enabled and at beginning of camera list
   */
  previousCamera() {
    if (
      !this.isImageLoading &&
      this.selectedSite &&
      this.selectedSite.Cameras.length > 0
    ) {
      let nextIndex: number;
      let currentIndex: number;

      if (this.selectedCamera) {
        currentIndex = this.selectedSite.Cameras.indexOf(this.selectedCamera);
        nextIndex = currentIndex - 1;
        if (nextIndex < 0) {
          if (this.autoChangeSite) nextIndex = -1;
          else nextIndex = this.selectedSite.Cameras.length - 1;
        }
      } else {
        nextIndex = 0;
      }

      if (nextIndex == -1) this.previousSite();
      else {
        this.selectedCamera = this.selectedSite.Cameras[nextIndex];
        this.router.navigate([
          '/view',
          this.selectedSite.Id,
          this.selectedCamera.Id,
        ]);
      }
    }
  }

  /**
   * Refresh the current camera image, with option to force refresh which also clears error state
   * @param {boolean} force - If true, also clear the camera's error state before refreshing
   */
  refreshCamera(force: boolean) {
    if (this.selectedCamera) {
      if (force) this.selectedCamera.HasError = false;
      this.appRefreshImageSource.next();
    }
  }

  /**
   * Cycle to the next site in the list
   */
  nextSite() {
    if (!this.isImageLoading && this.sites.length > 0) {
      let nextIndex: number;
      let currentIndex: number;

      if (this.selectedSite) {
        currentIndex = this.sites.indexOf(this.selectedSite);
        nextIndex = currentIndex + 1;
        if (nextIndex >= this.sites.length) nextIndex = 0;
      } else {
        nextIndex = 0;
      }

      this.selectedSite = this.sites[nextIndex];

      this.router.navigate([
        '/view',
        this.selectedSite.Id,
        this.selectedSite.Cameras.length > 0
          ? this.selectedSite.Cameras[0].Id
          : 0,
      ]);
    }
  }

  /**
   * Cycle to the previous site in the list
   */
  previousSite() {
    if (!this.isImageLoading && this.sites.length > 0) {
      let nextIndex: number;
      let currentIndex: number;

      if (this.selectedSite) {
        currentIndex = this.sites.indexOf(this.selectedSite);
        nextIndex = currentIndex - 1;
        if (nextIndex < 0) nextIndex = this.sites.length - 1;
      } else {
        nextIndex = 0;
      }

      this.selectedSite = this.sites[nextIndex];

      this.router.navigate([
        '/view',
        this.selectedSite.Id,
        this.selectedSite.Cameras.length > 0
          ? this.selectedSite.Cameras[this.selectedSite.Cameras.length - 1].Id
          : 0,
      ]);
    }
  }
}
